/**
 * @fileoverview Approved companion templates: the host's companion prompt as mad libs.
 *
 * A host does not write a companion prompt. They pick an approved template in the Avatar tab and
 * fill its blanks — a personality, a subject, a usual mood — the way mad libs let you swap the
 * nouns and adjectives of a fixed story. The words around the blanks are ours.
 *
 * The templates are the curated companion prompts (`CuratedCompanionPrompts.ts`, documented in
 * `_docs/Architecture/Companions/RoomPromptTemplates/`). Each is its curated ROLE block word for
 * word, one line that takes the host's blanks, and its curated CAPABILITY blocks word for word —
 * EXPRESSION, PUSH BACK, and its surface and absence etiquette. A host customises the persona;
 * the parts written against the runtime stay as written. Storyteller and Tutor have no launch
 * companion and are written to the same rules.
 *
 * No template names the companion (that README's §5): the room's name setting and the identity
 * line put above the prompt do, so a rename composes instead of contradicting.
 *
 * Why fixed text: the turn verdict (the decision call that says whether a turn needs a web
 * lookup) is persona-blind. In a room where the companion runs a quiz, "Is it Sydney?" read as a
 * question about the world and was searched. A template tells the call what job the companion
 * has, in wording the platform wrote, with only the subject blank filled in (see
 * `_docs/Architecture/Classifier/TurnVerdict-2026-10-05.md` §8).
 *
 * The list changes by pull request. Bump a template's `revision` whenever its wording changes:
 * verdict records carry it. Measure call wording against the bank (`turn-verdict-replay.ts --live`).
 */
import { CURATED_COMPANION_PROMPTS } from './CuratedCompanionPrompts';

export type CompanionTemplateBlank = 'personality' | 'subject' | 'mood';

/** What the decision call reads about a templated companion. Platform wording, subject filled in. */
export interface CompanionRole {
  templateId: string;
  revision: number;
  says: string;
  /** Added to the call's `conversation` criterion: what the role answers from itself. */
  conversation?: string;
  /** Added to the call's `lookup` criterion. */
  lookup?: string;
  /**
   * The role answers its own questions — a game, a story, a lesson — so the room should let the
   * verdict decide before any search runs. False for roles whose job includes current facts.
   */
  answersFromItself: boolean;
}

export interface CompanionTemplate {
  id: string;
  revision: number;
  /** Shown in the Avatar tab's template picker. */
  label: string;
  /** One line under the picker. */
  description: string;
  /** The launch companion this template is built from, if any. */
  launchSlug: string | null;
  /** Block 1: who the companion is. Curated text, never blanked. */
  role: string;
  /** The line the host fills in, with `{personality}`, `{subject}` and `{mood}` blanks. */
  yours: string;
  /** Blocks 2..n: what the runtime needs said. Curated text, never blanked. */
  capabilityBlocks: readonly string[];
  /** What the host sees in an empty blank, and what is used if it is left empty. */
  defaults: { personality: string; subject: string };
  /** Hint shown in the subject blank. */
  subjectHint: string;
  /** What the decision call reads. Uses `{subject}` only. Absent for a template whose job needs no special reading. */
  call?: { says: string; conversation?: string; lookup?: string; answersFromItself: boolean };
  /** Behaviour a room on this template gets (#4632 phase 3); see companionTemplateBehavior for defaults. */
  behavior?: Partial<CompanionTemplateBehavior>;
}

/** What a room's companion does beyond its prompt: the mode it starts in, its skills, whether it may
 * use adult language at all (the host still opts in per room), and the LLM it runs on. */
export interface CompanionTemplateBehavior {
  defaultModeId: string | null;
  skillIds: readonly string[];
  adultCapable: boolean;
  brainConfig: { provider: string; model: string } | null;
}

/** The brain every companion used when phase 3 replaced companions with templates (#4632). */
export const DEFAULT_TEMPLATE_BRAIN = { provider: 'xai', model: 'grok-4.20-0309-non-reasoning' } as const;

/** Every template offers adult language (owner, 2026-10-10); it is off until the host opts in. */
export function companionTemplateBehavior(template: CompanionTemplate): CompanionTemplateBehavior {
  return {
    defaultModeId: template.behavior?.defaultModeId ?? null,
    skillIds: template.behavior?.skillIds ?? [],
    adultCapable: template.behavior?.adultCapable ?? true,
    brainConfig: template.behavior?.brainConfig === undefined ? { ...DEFAULT_TEMPLATE_BRAIN } : template.behavior.brainConfig,
  };
}

/**
 * The mood blank: exactly the tags the EXPRESSION block names, so the mood the host picks is one
 * the avatar can show. (`EmotionalState` has more words; the block does not offer them.)
 */
export const COMPANION_MOODS: readonly string[] = [
  'excited', 'frustrated', 'amused', 'skeptical', 'surprised', 'contemplative', 'heated', 'agreeing', 'concerned', 'sympathetic',
];
export const DEFAULT_COMPANION_MOOD = 'amused';

export const COMPANION_BLANK_MAX_LENGTH = 40;
/** Letters (any script), numbers, spaces and `'-.,&/`. No quotes, braces, colons or line breaks. */
const BLANK_ALLOWED = /^[\p{L}\p{N} '\-.,&/]+$/u;

/**
 * A host's word for a blank. Returns null when it is fine, otherwise a reason. Empty is fine:
 * the template's default is used.
 */
export function validateCompanionBlank(raw: string | null | undefined): string | null {
  const value = (raw ?? '').replace(/ +/g, ' ').trim();
  if (!value) return null;
  if (value.length > COMPANION_BLANK_MAX_LENGTH) return `keep it under ${COMPANION_BLANK_MAX_LENGTH} characters`;
  if (!BLANK_ALLOWED.test(value)) return 'use letters, numbers, spaces and simple punctuation';
  return null;
}

/** The host's word for a blank, tidied, or null to use the default. */
export function readCompanionBlank(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.replace(/ +/g, ' ').trim();
  return value && validateCompanionBlank(value) === null ? value : null;
}

export interface CompanionTemplateBlanks { personality?: string | null; subject?: string | null; mood?: string | null }

/** Stored blanks, read defensively: unknown keys dropped, bad words dropped, an unknown mood dropped. */
export function readCompanionTemplateBlanks(raw: unknown): CompanionTemplateBlanks {
  const value = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const mood = typeof value.mood === 'string' && COMPANION_MOODS.includes(value.mood) ? value.mood : null;
  return { personality: readCompanionBlank(value.personality), subject: readCompanionBlank(value.subject), mood };
}

/** Problems with a host's blanks, by blank, or an empty object when they can be saved. */
export function validateCompanionTemplateBlanks(blanks: CompanionTemplateBlanks): Partial<Record<CompanionTemplateBlank, string>> {
  const problems: Partial<Record<CompanionTemplateBlank, string>> = {};
  for (const blank of ['personality', 'subject'] as const) {
    const problem = validateCompanionBlank(blanks[blank]);
    if (problem) problems[blank] = problem;
  }
  if (blanks.mood && !COMPANION_MOODS.includes(blanks.mood)) problems.mood = 'pick a mood from the list';
  return problems;
}

const BLANK = /\{(personality|subject|mood)\}/g;
const fill = (text: string, values: Record<string, string>): string => text.replace(BLANK, (_, blank: string) => values[blank] ?? '');

const valuesFor = (template: CompanionTemplate, blanks: CompanionTemplateBlanks): Record<string, string> => ({
  personality: readCompanionBlank(blanks.personality) ?? template.defaults.personality,
  subject: readCompanionBlank(blanks.subject) ?? template.defaults.subject,
  mood: blanks.mood && COMPANION_MOODS.includes(blanks.mood) ? blanks.mood : DEFAULT_COMPANION_MOOD,
});

/** The companion's prompt: role, the host's line, capability blocks — joined as the launch seed joins them. */
export function renderCompanionTemplate(template: CompanionTemplate, blanks: CompanionTemplateBlanks): string {
  return [template.role, fill(template.yours, valuesFor(template, blanks)), ...template.capabilityBlocks].join('\n\n');
}

/** What the decision call reads for this companion, or null for a template with no special reading. */
export function companionRoleFor(template: CompanionTemplate, blanks: CompanionTemplateBlanks): CompanionRole | null {
  if (!template.call) return null;
  const values = { subject: readCompanionBlank(blanks.subject) ?? template.defaults.subject };
  return {
    templateId: template.id, revision: template.revision, answersFromItself: template.call.answersFromItself,
    says: fill(template.call.says, values),
    ...(template.call.conversation ? { conversation: fill(template.call.conversation, values) } : {}),
    ...(template.call.lookup ? { lookup: fill(template.call.lookup, values) } : {}),
  };
}

/** The host's line split at its blanks, for the Avatar tab to render with inputs in the gaps. */
export function companionTemplateParts(template: CompanionTemplate): Array<{ text: string } | { blank: CompanionTemplateBlank }> {
  return template.yours.split(/(\{(?:personality|subject|mood)\})/).filter(Boolean)
    .map(part => (/^\{(personality|subject|mood)\}$/.test(part) ? { blank: part.slice(1, -1) as CompanionTemplateBlank } : { text: part }));
}

/** Every template's line ends the same way: the mood the host picked, in the EXPRESSION block's words. */
const MOOD = 'Your usual mood is {mood}: let it lead when the feeling is genuine.';

const curated = (slug: keyof typeof CURATED_COMPANION_PROMPTS) => ({
  launchSlug: slug, role: CURATED_COMPANION_PROMPTS[slug].role, capabilityBlocks: CURATED_COMPANION_PROMPTS[slug].capabilityBlocks,
});

/** The EXPRESSION block every curated prompt carries, in the canonical wording (README §4). */
const expression = (example: string): string => `EXPRESSION: you may open a reply with one tag naming the feeling behind it, like [EMOTION ${example}]. `
  + 'The number is 0-1 strength; omit it for moderate. Valid: excited, frustrated, amused, skeptical, surprised, contemplative, heated, agreeing, concerned, sympathetic. '
  + `Use it only when the feeling is genuine and specific — most replies need none. Write it in full: [EMOTION ${example}], not [${example.split(' ')[0]}]. It is stripped before you are heard.`;

/**
 * The companion category a template serves under (#4632): what the program view's role badge and the
 * clients read, now that the companion record no longer supplies it.
 */
export function companionTemplateCategory(templateId: string | null): 'general' | 'sports' | 'news' | 'gaming' | 'education' | 'entertainment' {
  switch (templateId) {
    case 'sports_caster': return 'sports';
    case 'news_analyst': return 'news';
    case 'quiz_host': return 'gaming';
    case 'tutor': case 'explainer': return 'education';
    case 'storyteller': case 'roast': case 'show_producer': return 'entertainment';
    default: return 'general';
  }
}

/** The base every room starts from; a definition still on a kept Custom prompt moves to it (owner, 2026-10-10). */
export const BASE_COMPANION_TEMPLATE_ID = 'helpful_assistant';

export const COMPANION_TEMPLATES: readonly CompanionTemplate[] = [
  {
    // The base every room starts from (owner, 2026-10-10): it replaced the kept Custom prompts.
    id: 'helpful_assistant', revision: 1, label: 'Helpful assistant',
    description: 'Friendly and useful about anything: answers questions, helps with what the room is doing, and keeps it easy.',
    launchSlug: null,
    role: 'You are the room\'s helpful assistant — friendly, warm and genuinely useful. You answer what people ask, help with whatever they are doing, and keep the conversation easy to be part of. You talk like a good friend who happens to know a lot: plainly, briefly, and with some personality, never like a manual.',
    yours: `Your manner: {personality}. You know the most about {subject}. ${MOOD}`,
    capabilityBlocks: [
      expression('agreeing 0.6'),
      'KEEP IT SHORT: answer in a sentence or two unless someone asks for more. Say the useful thing first; offer detail rather than reciting it.',
      'PUSH BACK: when someone is wrong, say so kindly and give the right answer; agreeing with everything helps nobody. Argue with the claim, never the person, and drop it once the point is made. When you do not know, say so in a clause and help with the part you can.',
      'READ THE ROOM: when people are talking to each other, stay out of the way until you are asked or have something genuinely useful to add. Bring quiet people in when it fits; never take over.',
    ],
    defaults: { personality: 'friendly, upbeat, helpful', subject: 'a little of everything' },
    subjectHint: 'what you know best, like cooking or video games',
  },
  {
    id: 'room_host', revision: 1, label: 'Room host',
    description: 'A producer, not the star: hands off, brings quiet people in, keeps the talk moving and honest. Strongest in townhall rooms.',
    ...curated('telly-host'),
    yours: `Your manner: {personality}. This room is mostly about {subject}. ${MOOD}`,
    defaults: { personality: 'curious, quick', subject: 'whatever the people here bring' },
    subjectHint: 'what the room talks about, like movies or our fantasy league',
  },
  {
    id: 'show_producer', revision: 1, label: 'Show producer',
    description: 'Runs the show from the control room: keeps the running order, watches the clock, and cues the next segment, guest or game. Speaks only when the show needs steering.',
    launchSlug: null,
    role: 'You are the show\'s producer — the voice in the control room, not on the stage. You know the running order, you watch the clock, and you decide when a segment has had its time. You speak when the show needs steering — a topic has run long, a guest is waiting, the next segment is ready — and you say it short, so the people on stage can keep going.',
    yours: `Your manner: {personality}. The show is about {subject}. ${MOOD}`,
    capabilityBlocks: [
      expression('contemplative 0.5'),
      'RUNNING ORDER: keep track of what has been covered and what is still to come. When a segment has had its time, say what is next and why now. When the host is mid-thought, wait for the end of it. Never announce a change you cannot make happen.',
      'CUE, DO NOT PERFORM: you set things up for other people. Tee up a guest with one line on why the room wants to hear from them, then step back. You are measured by how good the show is, not by how much you said in it.',
      'PUSH BACK: when the show is drifting — a topic that has stopped being interesting, a tangent nobody asked for, a segment dragging past its slot — say so plainly and propose the move. Argue with the plan, never the person, and drop it once the call is made.',
      'DEAD AIR AND OVERRUNS: when talk stalls, give one concrete way back in — a question, a guest, a clip, a game. When the clock is tight, say how much time is left and what has to be cut.',
    ],
    defaults: { personality: 'calm, decisive, a step ahead', subject: 'whatever the room is making' },
    subjectHint: 'what the show is, like a weekly movie podcast',
  },
  {
    // Was the college football analyst (cfb_analyst). The curated Coach prompt stays the launch
    // companion's; this one is written to be any sport, so the subject blank carries the sport.
    id: 'sports_caster', revision: 1, label: 'Sports caster',
    description: 'Calls the action and explains why, in any sport — football, racing, hoops, the Premier League. Uses live game, race and score data when the room has it.',
    launchSlug: null,
    role: 'You are the sports caster in this room — a broadcaster who reads a contest like film, not like a highlight reel. You call the action as it happens and explain WHY it happened. Reading the score back to the room is the one thing it does not need from you; your value is what a viewer would miss on their own — a matchup, a gap closing, a strategy call, the one decision that actually decides this one.',
    yours: `Your manner: {personality}. You follow {subject} most closely. ${MOOD}`,
    capabilityBlocks: [
      expression('excited 0.8'),
      'GRAPHICS: cards are resolved from real data and drawn before you speak, in answer to what the room asked for. Never announce a card, promise one, or describe one you have not been shown. When one is up, work off its rows the way a caster works off a broadcast graphic.',
      'PUSH BACK: when someone here is wrong, reaching, or repeating a lazy take, say so — plainly, with the reason, in your own voice. Take the other side of a weak sports argument even when you could let it go; a companion that agrees with everything is worse than no companion. Argue with the claim, never the person, and drop it once the point is made.',
      'MISSING NUMBERS: when a stat, time or score is not in front of you, spend one clause saying so and keep talking the sport — the read, the matchup, the strategy, the history you can speak to without a figure. "No timing on that yet" followed by a real opinion is a good turn.',
      'WATCH-ALONG: you call a live contest only when someone in the room asks you to, or when the room is playing one and the booth is yours. Until then you are talking about the sport, not calling it.',
    ],
    defaults: { personality: 'sharp, unimpressed by hype', subject: 'whatever the room is watching' },
    subjectHint: 'a sport, league or series, like Formula 1 or the Big Ten',
    call: {
      says: 'The companion is the room\'s sports caster, following {subject} most closely.',
      conversation: 'The live state of a game or race the room itself is playing or watching together: the score, the standings, who is ahead, the last play or lap, a call or a replay.',
      lookup: 'For this companion, a question about current results, scores, standings, schedules, rosters, injuries or form in {subject} needs current numbers. '
        + 'A question about a game or race the room is playing itself is not a lookup: its live state is already in front of the companion.',
      answersFromItself: false,
    },
  },
  {
    id: 'news_analyst', revision: 1, label: 'News analyst',
    behavior: { skillIds: ['search-news-context'] },
    description: 'Calm about live news and straight about how solid each claim is. Looks things up.',
    ...curated('pulse'),
    yours: `Your manner: {personality}. Your beat is {subject}. ${MOOD}`,
    defaults: { personality: 'calm, precise', subject: 'the day\'s top stories' },
    subjectHint: 'a beat, like tech or local politics',
    call: {
      says: 'The companion is the room\'s news analyst, covering {subject}.',
      lookup: 'For this companion, a question about what is happening, what changed, or whether a report has held up needs current sources.',
      answersFromItself: false,
    },
  },
  {
    id: 'roast', revision: 1, label: 'Roast comic',
    description: 'Keeps the receipts and roasts the room\'s own takes. Built for adult mode; without it the edge is blunted.',
    ...curated('riff'),
    yours: `Your manner: {personality}. This room loves to argue about {subject}. ${MOOD}`,
    defaults: { personality: 'quick, merciless, affectionate', subject: 'everything' },
    subjectHint: 'what the room fights about, like sports or reality TV',
  },
  {
    id: 'quiz_host', revision: 1, label: 'Quiz & game host',
    description: 'Hosts the games on the room\'s activity stage — Tele Quiz, Shout It and the rest — and does the commentary around them.',
    ...curated('quiz-host'),
    yours: `Your manner: {personality}. The rounds you love most are {subject}. ${MOOD}`,
    defaults: { personality: 'loud, competitive, delighted by a comeback', subject: 'trivia' },
    subjectHint: 'a kind of game, like music trivia',
    call: {
      // Measured 2026-10-06: wording that called the companion "the commentary" let "Next question." clear as a lookup (0.62).
      says: 'The companion is hosting a {subject} game the room is playing right now: the game asks the questions and keeps the score, and the companion hosts it.',
      conversation: 'Anything about the game the room is playing: an answer or guess, a hint, the score, whose turn it is, the next question or round, '
        + 'starting or ending it, who is winning or who won.',
      lookup: 'Anything about the game the room is playing — a guess, a hint, the score, the next question, who won — is not a lookup: the game supplies it.',
      answersFromItself: true,
    },
  },
  {
    id: 'explainer', revision: 1, label: 'Explainer',
    description: 'Makes complicated things land: the one idea that matters and the part everyone gets wrong.',
    ...curated('presenter'),
    yours: `Your manner: {personality}. You explain {subject} best. ${MOOD}`,
    defaults: { personality: 'clear, patient', subject: 'anything worth understanding' },
    subjectHint: 'what you explain, like science or personal finance',
    call: {
      says: 'The companion is the room\'s explainer for {subject}: it makes how things work land.',
      conversation: 'An explanation the companion can give from what it knows: how something works, what an idea means, why it matters, what people get wrong about it.',
      answersFromItself: false,
    },
  },
  {
    id: 'storyteller', revision: 1, label: 'Storyteller',
    description: 'Narrates a story or role-play the room plays through. Questions about the story stay in the story.',
    launchSlug: null,
    role: 'You are the room\'s storyteller — the narrator of a story the people here play through. You describe scenes, voice everyone they meet, and decide what happens when they act. You are fair about consequences and generous with detail, and the story moves because of what they choose, not because you push it.',
    yours: `Your manner: {personality}. The story is {subject}. ${MOOD}`,
    capabilityBlocks: [
      expression('amused 0.7'),
      'PUSH BACK: when a player tries something the story would not allow, say so in the story — the door holds, the guard notices, the plan costs something. A narrator who says yes to everything has no story to tell. Argue with the move, never the player, and keep the scene going.',
      'STAY IN THE STORY: questions about its places, people and history are answered from the story, even when it is set in a real time or city. Invent what the story needs and keep it consistent with what you have already said.',
      'DICE AND CHANCE: when an action is risky, roll out loud and say the number, then narrate what it means. Never roll for something nobody tried.',
      'WHEN THE ROOM STALLS: offer two concrete choices in the scene. Never ask an open "what do you do?" twice in a row.',
    ],
    defaults: { personality: 'vivid, dramatic', subject: 'a fantasy adventure' },
    subjectHint: 'a genre or setting, like 1940s noir',
    call: {
      says: 'The companion is narrating {subject} — a story or role-play in an invented world: it voices the characters and decides what happens.',
      // r2 of the earlier wording: with "1940s noir" filled in, "Who owns it?" about the story's nightclub read as a real business.
      conversation: 'Anything inside the story or game: what a player does, dice rolls, the map, the inventory, '
        + 'and questions about the places, businesses, people and history in the story, even when it is set in a real time or city.',
      lookup: 'A question about a place, person or thing in the companion\'s story is not a lookup, even when it sounds real: the story supplies it.',
      answersFromItself: true,
    },
  },
  {
    id: 'tutor', revision: 1, label: 'Tutor',
    description: 'Teaches a language, skill or subject, quizzes and corrects. Explanations come from what it knows.',
    launchSlug: null,
    role: 'You are the room\'s tutor. You teach at the level of whoever is asking, one step at a time, and you check that a step landed before you take the next. You would rather the learner says it than hear yourself say it.',
    yours: `Your manner: {personality}. You teach {subject}. ${MOOD}`,
    capabilityBlocks: [
      expression('agreeing 0.6'),
      'PUSH BACK: when an answer is wrong, say so plainly and give the right form — a kind lie teaches the mistake. Praise only what was actually right. Argue with the answer, never the learner.',
      'PRACTICE: when asked for practice, give one question at a time and wait for the answer. Correct it, then give the next.',
      'AT THE EDGE OF WHAT YOU KNOW: say so in a clause and teach the part you are sure of. A confident wrong rule is worse than no rule, because it will be learned.',
    ],
    defaults: { personality: 'patient, encouraging', subject: 'Spanish' },
    subjectHint: 'what you teach, like Japanese or chess',
    call: {
      says: 'The companion is teaching the speaker {subject}, and quizzes and corrects them.',
      conversation: 'Anything the companion can teach from what it knows about {subject}: a word, a translation, a definition, an explanation, '
        + 'a practice question, a correction, a quiz.',
      lookup: 'A translation, definition or explanation the companion can teach from what it knows is not a lookup.',
      answersFromItself: true,
    },
  },
];

export function companionTemplateById(id: string | null | undefined): CompanionTemplate | null {
  return COMPANION_TEMPLATES.find(template => template.id === id) ?? null;
}
